// No i18n JSX runtime: live AI text is never translated.
import React, { memo, useEffect, useLayoutEffect, useRef, useState } from 'react';

// Typewriter for live text: the server sends fragments every ~400 ms; they
// are buffered here and revealed character by character at an adaptive speed
// (faster when the backlog grows, so the display never lags far behind).
const BASE_CPS = 90; // characters per second when keeping up
const CATCH_UP_S = 0.5; // a backlog is absorbed in about this long
const FINISH_S = 0.25; // once the text is complete, reveal the rest quickly

const now = () => (typeof performance !== 'undefined' && performance.now ? performance.now() : Date.now());
const nextFrame = (cb) => (typeof window.requestAnimationFrame === 'function' ? window.requestAnimationFrame(cb) : setTimeout(() => cb(now()), 16));
const cancelFrame = (id) => (typeof window.cancelAnimationFrame === 'function' ? window.cancelAnimationFrame(id) : clearTimeout(id));

// Never cut a surrogate pair (emoji) in half.
const safeEnd = (text, n) => (n > 0 && n < text.length && /[\uD800-\uDBFF]/.test(text[n - 1]) ? n + 1 : n);

export function useTypewriter(target, complete) {
  const [shown, setShown] = useState(0);
  const pos = useRef(0);
  const targetRef = useRef(target);
  const completeRef = useRef(complete);
  targetRef.current = target;
  completeRef.current = complete;

  useEffect(() => {
    if (pos.current > target.length) pos.current = 0; // text restarted
    if (Math.floor(pos.current) >= target.length) return undefined;
    let frame;
    let last = now();
    const tick = (ts) => {
      const t = typeof ts === 'number' ? ts : now();
      const dt = Math.min(0.1, Math.max(0, (t - last) / 1000));
      last = t;
      const text = targetRef.current;
      const backlog = text.length - pos.current;
      const speed = completeRef.current ? Math.max(BASE_CPS * 3, backlog / FINISH_S) : Math.max(BASE_CPS, backlog / CATCH_UP_S);
      pos.current = Math.min(text.length, pos.current + speed * dt);
      setShown(safeEnd(text, Math.floor(pos.current)));
      if (pos.current < text.length) frame = nextFrame(tick);
    };
    frame = nextFrame(tick);
    return () => cancelFrame(frame);
  }, [target, complete]);

  return target.slice(0, Math.min(shown, target.length));
}

// Light cleanup of the line being typed, shown as plain text until its
// paragraph is complete and rendered as markdown.
const plainTail = (s) => s
  .replace(/^#{1,6}\s+/gm, '')
  .replace(/\*\*|__|`/g, '')
  .replace(/^\s*[-*]\s+/gm, '• ');

/**
 * LiveTyping renders a live text: complete paragraphs through the (memoized)
 * markdown renderer, the paragraph being typed as plain text with a caret.
 * onGrow is called after each visible growth (to keep the view pinned).
 */
const LiveTyping = ({ text, complete, Markdown, onGrow, caret = true }) => {
  const shown = useTypewriter(text, complete);
  const cut = shown.lastIndexOf('\n\n');
  const settled = cut >= 0 ? shown.slice(0, cut) : '';
  const tail = cut >= 0 ? shown.slice(cut + 2) : shown;
  useLayoutEffect(() => { onGrow?.(); }, [shown, onGrow]);
  return (
    <div data-testid="live-typing" data-typed={shown.length} data-target={text.length}>
      {settled && <SettledMarkdown text={settled} Markdown={Markdown} />}
      <p className="whitespace-pre-wrap text-[15px] leading-[1.7] text-fg-body [overflow-wrap:anywhere]">
        {plainTail(tail)}
        {caret && <span className="ml-0.5 inline-block h-4 w-1.5 animate-pulse rounded-sm bg-gold-400 align-middle" aria-hidden="true" />}
      </p>
    </div>
  );
};

// Re-parsed only when a paragraph is completed, not on every character.
const SettledMarkdown = memo(({ text, Markdown }) => <Markdown>{text}</Markdown>);

export default LiveTyping;
